import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { PaymentService, PaymentMethodDto } from '../services/payment.service';
import { AuthService } from '../services/auth.service';
import { BillingService, SubscriptionStatus } from '../services/billing.service';
import { STRIPE_APPEARANCE } from '../services/stripe-appearance';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.css']
})
export class PaymentsComponent implements OnInit, OnDestroy {
  @ViewChild('paymentElementRef') paymentElementRef?: ElementRef<HTMLDivElement>;

  paymentMethods: PaymentMethodDto[] = [];
  subscription: SubscriptionStatus | null = null;
  showForm = false;
  loading = false;
  saving = false;
  elementReady = false;
  elementComplete = false;
  busyMethodId: string | null = null;
  confirmDeleteId: string | null = null;
  planBusy = false;
  confirmCancel = false;
  errorMessage = '';
  successMessage = '';

  private user: any = null;
  private stripeElements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  constructor(
    private router: Router,
    private paymentService: PaymentService,
    private billingService: BillingService,
    private authService: AuthService
  ) {}

  get isProvider(): boolean {
    return this.user?.role === 'provider' || this.user?.role === 'admin';
  }

  get planName(): string {
    const tier = this.subscription?.tier || 'free';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  get hasPaidPlan(): boolean {
    const s = this.subscription;
    return !!s && s.tier !== 'free' && (s.status === 'active' || s.status === 'trialing' || s.status === 'past_due');
  }

  ngOnInit(): void {
    this.user = this.authService.getCurrentUser();
    this.loadPaymentMethods();
    if (this.isProvider) this.loadSubscription();
  }

  ngOnDestroy(): void {
    this.paymentElement?.destroy();
  }

  loadPaymentMethods(): void {
    if (!this.user) return;
    this.loading = true;
    this.paymentService.getPaymentMethods(this.user.id).subscribe({
      next: methods => {
        this.paymentMethods = methods;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Could not load payment methods. Please try again later.';
      }
    });
  }

  loadSubscription(): void {
    this.billingService.getSubscription(this.user.id).subscribe({
      next: sub => this.subscription = sub,
      error: () => {}
    });
  }

  openForm(): void {
    this.showForm = true;
    this.errorMessage = '';
    this.successMessage = '';
    setTimeout(() => this.mountPaymentElement());
  }

  closeForm(): void {
    this.showForm = false;
    this.paymentElement?.destroy();
    this.paymentElement = null;
    this.elementReady = false;
    this.elementComplete = false;
  }

  private async mountPaymentElement(): Promise<void> {
    if (!this.paymentElementRef || this.paymentElement) return;
    const stripe = await this.paymentService.getStripe();
    if (!stripe) {
      this.errorMessage = 'Payments are not configured yet. Please contact support.';
      return;
    }
    this.stripeElements = stripe.elements({
      mode: 'setup',
      currency: 'usd',
      paymentMethodTypes: ['card'],
      appearance: STRIPE_APPEARANCE,
    });
    this.paymentElement = this.stripeElements.create('payment', {
      layout: 'tabs',
      defaultValues: {
        billingDetails: {
          name: [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' '),
          email: this.user?.email || '',
        },
      },
      terms: { card: 'never' },
    });
    this.paymentElement.on('ready', () => this.elementReady = true);
    this.paymentElement.on('change', e => this.elementComplete = e.complete);
    this.paymentElement.mount(this.paymentElementRef.nativeElement);
  }

  async addPaymentMethod(): Promise<void> {
    if (!this.user || !this.stripeElements || this.saving) return;
    this.saving = true;
    this.errorMessage = '';

    try {
      const stripe = await this.paymentService.getStripe();
      if (!stripe) throw new Error('Payments are not configured yet. Please contact support.');

      const { error: submitError } = await this.stripeElements.submit();
      if (submitError) throw new Error(submitError.message);

      const { clientSecret } = await firstValueFrom(this.paymentService.createSetupIntent(this.user.id));
      const { error, setupIntent } = await stripe.confirmSetup({
        elements: this.stripeElements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });
      if (error) throw new Error(error.message);

      // First card on the account becomes the default so subscriptions/bookings can use it.
      const pmId = typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : setupIntent?.payment_method?.id;
      if (pmId && !this.paymentMethods.some(m => m.isDefault)) {
        await firstValueFrom(this.paymentService.setDefaultPaymentMethod(this.user.id, pmId));
      }

      this.closeForm();
      this.successMessage = 'Card saved.';
      this.loadPaymentMethods();
      if (this.isProvider) this.loadSubscription();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Could not save card. Please try again.';
    } finally {
      this.saving = false;
    }
  }

  setDefault(methodId: string): void {
    if (!this.user) return;
    this.busyMethodId = methodId;
    this.paymentService.setDefaultPaymentMethod(this.user.id, methodId).subscribe({
      next: () => {
        this.busyMethodId = null;
        this.loadPaymentMethods();
        if (this.isProvider) this.loadSubscription();
      },
      error: () => {
        this.busyMethodId = null;
        this.errorMessage = 'Could not update default payment method.';
      }
    });
  }

  deletePaymentMethod(methodId: string): void {
    if (this.confirmDeleteId !== methodId) {
      this.confirmDeleteId = methodId;
      return;
    }
    this.confirmDeleteId = null;
    this.busyMethodId = methodId;
    this.paymentService.deletePaymentMethod(methodId).subscribe({
      next: () => {
        this.busyMethodId = null;
        this.loadPaymentMethods();
      },
      error: () => {
        this.busyMethodId = null;
        this.errorMessage = 'Could not delete payment method.';
      }
    });
  }

  cancelPlan(): void {
    if (!this.confirmCancel) {
      this.confirmCancel = true;
      return;
    }
    this.confirmCancel = false;
    this.planBusy = true;
    this.billingService.cancelSubscription(this.user.id).subscribe({
      next: sub => {
        this.planBusy = false;
        this.subscription = { ...this.subscription!, ...sub };
      },
      error: err => {
        this.planBusy = false;
        this.errorMessage = err?.error?.message || 'Could not cancel your plan. Please try again.';
      }
    });
  }

  resumePlan(): void {
    this.planBusy = true;
    this.billingService.resumeSubscription(this.user.id).subscribe({
      next: sub => {
        this.planBusy = false;
        this.subscription = { ...this.subscription!, ...sub };
      },
      error: err => {
        this.planBusy = false;
        this.errorMessage = err?.error?.message || 'Could not resume your plan. Please try again.';
      }
    });
  }

  upgrade(): void {
    this.router.navigate(['/plans', 'pro']);
  }

  isExpired(m: PaymentMethodDto): boolean {
    const now = new Date();
    return m.expiryYear < now.getFullYear() || (m.expiryYear === now.getFullYear() && m.expiryMonth < now.getMonth() + 1);
  }

  brandLabel(brand: string): string {
    const map: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' };
    return map[brand?.toLowerCase()] || (brand ? brand[0].toUpperCase() + brand.slice(1) : 'Card');
  }

  goBack(): void {
    this.router.navigate([this.isProvider ? '/provider-dashboard' : '/dashboard']);
  }
}
